import React from 'react';
import { reportError } from '../utils/errorReporter';

/**
 * On-brand, unconditional fallback for unhandled render-phase errors.
 *
 * There is deliberately no raw-dump / detailed-stack rendering path here —
 * that branch was removed entirely (not merely left unused) per Brandon's
 * directive: "Raw stack traces/error dumps must NEVER be shown to the user,
 * in ANY environment... structurally unreachable, not just currently
 * unreached." Full error detail (message/stack/componentStack) still reaches
 * server logs and telemetry via reportError() below — this only removes the
 * user-facing leak, not any diagnostic capability. A developer running
 * `vite dev` locally still sees the full error via `console.error('[RK-CRASH]', ...)`
 * in devtools.
 *
 * There used to be a `friendly` prop selecting between this fallback and a
 * raw-dump fallback; it has been removed rather than kept as an unused,
 * silently-no-op default, since a future caller passing `friendly={false}`
 * expecting the old raw-dump behavior back is exactly the failure mode
 * Brandon's directive calls out to avoid.
 */
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[RK-CRASH]', error.message, error.stack, info.componentStack);
    try {
      reportError(error.message, {
        stack: error.stack,
        componentStack: info.componentStack,
        kind: 'react_render',
      });
    } catch {
      // reporting failure must never break the fallback render
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, textAlign: 'center', fontFamily: 'sans-serif' }}>
          <p style={{ fontSize: 16, marginBottom: 16 }}>
            Something went wrong &mdash; the error has been reported. Try reloading.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '8px 20px', fontSize: 14, cursor: 'pointer' }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
